import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // If already authenticated as admin, go straight to dashboard
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => {
        if (res.data?.role === 'admin') navigate('/admin', { replace: true });
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/admin/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      navigate('/admin', { state: { user: data }, replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0F1A14' }}>
        <div className="w-10 h-10 border-4 border-[#34D399] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0F1A14' }}>
      {/* Subtle radial glow */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(52,211,153,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src={NOVA_LOGO} alt="Nova Foods" className="h-14 w-14 rounded-2xl object-cover mb-4 shadow-lg" />
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#34D399]/70 mb-1">Nova Foods</p>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Admin Portal</h1>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8 shadow-2xl"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-stone-400 mb-2 uppercase tracking-wider">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                placeholder="admin@thenovafoods.com"
                data-testid="admin-email-input"
                className="w-full px-4 py-3 rounded-xl text-white placeholder-stone-600 text-sm outline-none transition-all focus:ring-2 focus:ring-[#34D399]/40"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-400 mb-2 uppercase tracking-wider">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                data-testid="admin-password-input"
                className="w-full px-4 py-3 rounded-xl text-white placeholder-stone-600 text-sm outline-none transition-all focus:ring-2 focus:ring-[#34D399]/40"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>

            {error && (
              <p
                data-testid="admin-login-error"
                className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2 text-center"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50"
              style={{
                background: loading ? 'rgba(52,211,153,0.5)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
                color: '#0F1A14',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-stone-600 mt-6">
          Nova Foods &mdash; Internal Use Only
        </p>
      </div>
    </div>
  );
}
